import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

export interface CycleSummary {
  cycleName: string;
  period: string;
  retailerName: string;
  totalStores: number;
  totalCampaigns: number;
  totalTasks: number;
  status: "active" | "completed" | "signed_off" | "pending_signoff";
  completedAt?: string | null;
  signedOffBy?: string | null;
  campaigns: Array<{
    id: number;
    name: string;
    storeCount: number;
    taskCount: number;
  }>;
}

/**
 * Hook to fetch cycle summary data for an operator
 * @param operatorId - The operator's user ID
 * @param cycleId - The cycle ID to fetch summary for
 */
export function useCycleSummary(operatorId: number | string | undefined, cycleId: number | string | undefined) {
  return useQuery<CycleSummary>({
    queryKey: ['cycleSummary', operatorId, cycleId],
    queryFn: async () => {
      if (!operatorId || !cycleId) {
        console.warn('[useCycleSummary] Missing operatorId or cycleId:', { operatorId, cycleId });
        throw new Error('Operator ID and Cycle ID are required');
      }

      console.log('[useCycleSummary] Fetching cycle summary:', { operatorId, cycleId });
      const response = await apiFetch(`/api/mobile/cycle-summary/${operatorId}/${cycleId}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[useCycleSummary] API error:', { status: response.status, error: errorData });
        throw new Error(errorData.error || `Failed to fetch cycle summary: ${response.status}`);
      }

      const data = await response.json();
      console.log('[useCycleSummary] Cycle summary received:', { status: data.status, totalTasks: data.totalTasks });
      return data;
    },
    enabled: !!operatorId && !!cycleId,
    staleTime: 5_000, // 5 seconds - reduced for faster updates after completion
    refetchOnWindowFocus: true, // Refetch when window regains focus
    retry: 2,
  });
}

